<template>
    <a-modal
        :visible="visible"
        centered
        width="620px"
        class="email-validation-modal"
        :footer="null"
        @cancel="handleClose"
    >
        <div class="email-validation">
            <div class="email-validation__icon">
                <span class="material-icons">verified_user</span>
            </div>
            <h2>E-posta adresinizi doğrulayın</h2>
            <p class="email-validation__lead">
                Hesabınızın güvenliği ve kritik işlemlerde kimlik doğrulamasının sağlanması için
                e-posta adresinizi doğrulamanız gerekiyor.
            </p>

            <div class="email-validation__notice">
                <p>
                    <strong>1 Temmuz 2026</strong> tarihinden sonra e-postası doğrulanmamış kullanıcılar
                    Supsis paneline giriş yapamayacak ve entegrasyon oluşturma gibi güvenlik açısından
                    hassas işlemleri tamamlayamayacaktır.
                </p>
                <p>
                    Ayrıca merkezi giriş ekranı <strong>login.supsis.com</strong> ve
                    <strong>supsis.ai</strong> sitesindeki “Giriş Yap” butonu yalnızca e-posta adresi
                    doğrulanmış kullanıcılar tarafından kullanılabilecektir.
                </p>
            </div>

            <template v-if="step === 1">
                <div class="email-validation__step">
                    <label>Doğrulanacak e-posta adresi</label>
                    <a-input :value="user && user.email" disabled size="large">
                        <a-icon slot="prefix" type="mail" />
                    </a-input>
                </div>
            </template>
            <template v-else>
                <div class="email-validation__step email-validation__step--pin">
                    <div class="email-validation__pin-card">
                        <div class="email-validation__pin-header">
                            <div>
                                <label>6 haneli doğrulama kodu</label>
                                <p v-if="user && user.email" class="email-validation__sent-to">
                                    <span class="material-icons">mark_email_read</span>
                                    Kod <strong>{{ user.email }}</strong> adresine gönderildi
                                </p>
                            </div>
                            <div
                                class="email-validation__timer"
                                :class="{ 'email-validation__timer--expired': isCodeExpired }"
                            >
                                <span class="material-icons">{{ isCodeExpired ? "schedule" : "timer" }}</span>
                                <div>
                                    <small>{{ isCodeExpired ? "Süre doldu" : "Kalan süre" }}</small>
                                    <strong class="email-validation__timer-value">{{ formattedCountdown }}</strong>
                                </div>
                            </div>
                        </div>

                        <div
                            class="email-validation__pin-group"
                            @paste.prevent="handlePinPaste"
                        >
                            <input
                                v-for="(digit, index) in pinDigits"
                                :key="index"
                                :ref="'pinBox' + index"
                                :value="digit"
                                type="text"
                                inputmode="numeric"
                                maxlength="1"
                                class="email-validation__pin-box"
                                :class="{
                                    'email-validation__pin-box--filled': !!digit,
                                    'email-validation__pin-box--error': pinError,
                                }"
                                :disabled="isCodeExpired"
                                autocomplete="one-time-code"
                                @input="onPinInput(index, $event)"
                                @keydown="onPinKeydown(index, $event)"
                                @focus="onPinFocus(index)"
                            />
                        </div>

                        <div class="email-validation__timer-progress">
                            <span :style="{ width: countdownProgress + '%' }"></span>
                        </div>
                        <p class="email-validation__pin-helper">
                            <template v-if="isCodeExpired">
                                Kodun süresi doldu. Yeni kod göndererek tekrar deneyin.
                            </template>
                            <template v-else>
                                Kod 5 dakika boyunca geçerlidir. Kodu kimseyle paylaşmayın.
                            </template>
                        </p>
                    </div>

                    <button
                        type="button"
                        class="email-validation__resend"
                        :disabled="loading || (!isCodeExpired && countdownSeconds > 240)"
                        @click="resendCode"
                    >
                        Kodu tekrar gönder
                        <span v-if="!isCodeExpired && countdownSeconds > 240">
                            ({{ formattedResendWait }})
                        </span>
                    </button>
                </div>
            </template>

            <div class="email-validation__actions">
                <a-button size="large" @click="handleClose">Daha sonra</a-button>
                <a-button
                    v-if="step === 1"
                    type="primary"
                    size="large"
                    :loading="loading"
                    @click="sendCode"
                >
                    Doğrulama kodu gönder
                </a-button>
                <a-button
                    v-else
                    type="primary"
                    size="large"
                    :loading="loading"
                    :disabled="isCodeExpired || pin.length !== 6"
                    @click="verifyCode"
                >
                    E-postamı doğrula
                </a-button>
            </div>
        </div>
    </a-modal>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters, mapState } from "vuex";

var PIN_LENGTH = 6;
var CODE_TTL_SECONDS = 5 * 60;
var RESEND_COOLDOWN_SECONDS = 60;

export default {
    name: "EmailValidationModal",
    data() {
        return {
            visible: false,
            step: 1,
            pinDigits: ["", "", "", "", "", ""],
            pinError: false,
            loading: false,
            reminderTimer: null,
            countdownSeconds: 0,
            countdownTimer: null,
        };
    },
    computed: {
        ...mapState({
            user: function (state) {
                return state.auth.user;
            },
            isAdmin: function (state) {
                return state.auth.adminUser != null;
            },
        }),
        ...mapGetters({
            isLoggedIn: "auth/isLoggedIn",
        }),
        pin: function () {
            return this.pinDigits.join("");
        },
        isCodeExpired: function () {
            return this.step === 2 && this.countdownSeconds <= 0;
        },
        formattedCountdown: function () {
            var minutes = Math.floor(this.countdownSeconds / 60);
            var seconds = this.countdownSeconds % 60;
            var paddedSeconds = seconds < 10 ? "0" + seconds : String(seconds);
            var paddedMinutes = minutes < 10 ? "0" + minutes : String(minutes);
            return paddedMinutes + ":" + paddedSeconds;
        },
        formattedResendWait: function () {
            var wait = this.countdownSeconds - (CODE_TTL_SECONDS - RESEND_COOLDOWN_SECONDS);
            if (wait <= 0) return "";
            if (wait >= 60) {
                return Math.ceil(wait / 60) + " dk";
            }
            return wait + " sn";
        },
        countdownProgress: function () {
            if (this.countdownSeconds <= 0) return 0;
            return Math.round((this.countdownSeconds / CODE_TTL_SECONDS) * 100);
        },
    },
    watch: {
        isLoggedIn: function () {
            this.scheduleEmailValidationReminder();
        },
        user: function () {
            this.scheduleEmailValidationReminder();
        },
        "user.emailValidated": function () {
            this.scheduleEmailValidationReminder();
        },
        step: function (val) {
            if (val === 2) {
                var self = this;
                this.$nextTick(function () {
                    self.focusPinBox(0);
                });
            }
        },
    },
    mounted: function () {
        this.scheduleEmailValidationReminder();
        this.$root.$on("show-email-validation-modal", this.openModal);
    },
    beforeDestroy: function () {
        if (this.reminderTimer) clearTimeout(this.reminderTimer);
        this.stopCountdown();
        this.$root.$off("show-email-validation-modal", this.openModal);
    },
    methods: {
        ...mapActions({
            sendEmailValidationPin: "user/sendEmailValidationPin",
            verifyEmailValidationPin: "user/verifyEmailValidationPin",
            refreshAuthUser: "auth/refreshAuthUser",
        }),
        shouldShowEmailValidationReminder: function () {
            if (!this.isLoggedIn || this.isAdmin || !this.user) return false;
            if (this.user.emailValidated === true) return false;
            var key = "email-validation-reminder-" + this.user._id;
            return localStorage.getItem(key) !== moment().format("YYYY-MM-DD");
        },
        scheduleEmailValidationReminder: function () {
            if (this.reminderTimer) clearTimeout(this.reminderTimer);
            if (!this.shouldShowEmailValidationReminder()) return;
            var self = this;
            this.reminderTimer = setTimeout(function () {
                if (!self.shouldShowEmailValidationReminder()) return;
                localStorage.setItem(
                    "email-validation-reminder-" + self.user._id,
                    moment().format("YYYY-MM-DD")
                );
                self.openModal();
            }, 60 * 1000);
        },
        resetPin: function () {
            this.pinDigits = ["", "", "", "", "", ""];
            this.pinError = false;
        },
        openModal: function () {
            this.step = 1;
            this.resetPin();
            this.stopCountdown();
            this.visible = true;
        },
        handleClose: function () {
            this.visible = false;
            this.stopCountdown();
        },
        startCountdown: function () {
            this.stopCountdown();
            this.countdownSeconds = CODE_TTL_SECONDS;
            var self = this;
            this.countdownTimer = setInterval(function () {
                if (self.countdownSeconds <= 0) {
                    self.stopCountdown();
                    return;
                }
                self.countdownSeconds -= 1;
            }, 1000);
        },
        stopCountdown: function () {
            if (this.countdownTimer) {
                clearInterval(this.countdownTimer);
                this.countdownTimer = null;
            }
        },
        focusPinBox: function (index) {
            var ref = this.$refs["pinBox" + index];
            var el = Array.isArray(ref) ? ref[0] : ref;
            if (el) el.focus();
        },
        onPinFocus: function (index) {
            var el = this.$refs["pinBox" + index];
            var input = Array.isArray(el) ? el[0] : el;
            if (input) input.select();
        },
        onPinInput: function (index, event) {
            if (this.isCodeExpired) return;
            var value = (event.target.value || "").replace(/\D/g, "");
            this.pinError = false;

            if (value.length > 1) {
                this.fillPinFromString(value, index);
                return;
            }

            this.$set(this.pinDigits, index, value.slice(-1));

            if (value && index < PIN_LENGTH - 1) {
                this.focusPinBox(index + 1);
            }

            if (this.pin.length === PIN_LENGTH) {
                this.verifyCode();
            }
        },
        onPinKeydown: function (index, event) {
            if (event.key === "Enter") {
                this.verifyCode();
                return;
            }
            if (event.key === "Backspace") {
                if (this.pinDigits[index]) {
                    this.$set(this.pinDigits, index, "");
                    return;
                }
                if (index > 0) {
                    event.preventDefault();
                    this.$set(this.pinDigits, index - 1, "");
                    this.focusPinBox(index - 1);
                }
            }
            if (event.key === "ArrowLeft" && index > 0) {
                event.preventDefault();
                this.focusPinBox(index - 1);
            }
            if (event.key === "ArrowRight" && index < PIN_LENGTH - 1) {
                event.preventDefault();
                this.focusPinBox(index + 1);
            }
        },
        fillPinFromString: function (value, startIndex) {
            var digits = value.replace(/\D/g, "").slice(0, PIN_LENGTH);
            var self = this;
            digits.split("").forEach(function (digit, offset) {
                var targetIndex = startIndex + offset;
                if (targetIndex < PIN_LENGTH) {
                    self.$set(self.pinDigits, targetIndex, digit);
                }
            });
            var nextIndex = Math.min(startIndex + digits.length, PIN_LENGTH - 1);
            this.focusPinBox(nextIndex);
            if (this.pin.length === PIN_LENGTH) {
                this.verifyCode();
            }
        },
        handlePinPaste: function (event) {
            if (this.isCodeExpired) return;
            var pasted = "";
            if (event.clipboardData) {
                pasted = event.clipboardData.getData("text") || "";
            }
            this.fillPinFromString(pasted, 0);
        },
        sendCode: function () {
            var self = this;
            this.loading = true;
            this.sendEmailValidationPin()
                .then(function () {
                    self.step = 2;
                    self.resetPin();
                    self.startCountdown();
                    self.$message.success("Doğrulama kodu e-posta adresinize gönderildi.");
                })
                .catch(function () {
                    self.$message.error("Doğrulama kodu gönderilemedi.");
                })
                .finally(function () {
                    self.loading = false;
                });
        },
        resendCode: function () {
            if (this.loading) return;
            if (!this.isCodeExpired && this.countdownSeconds > CODE_TTL_SECONDS - RESEND_COOLDOWN_SECONDS) {
                return;
            }
            var self = this;
            this.loading = true;
            this.sendEmailValidationPin()
                .then(function () {
                    self.resetPin();
                    self.startCountdown();
                    self.$message.success("Yeni doğrulama kodu gönderildi.");
                    self.$nextTick(function () {
                        self.focusPinBox(0);
                    });
                })
                .catch(function () {
                    self.$message.error("Doğrulama kodu gönderilemedi.");
                })
                .finally(function () {
                    self.loading = false;
                });
        },
        verifyCode: function () {
            if (this.isCodeExpired) {
                this.$message.warning("Doğrulama kodunun süresi doldu. Lütfen yeni kod isteyin.");
                return;
            }
            if (!/^[0-9]{6}$/.test(this.pin)) {
                this.pinError = true;
                this.$message.error("Lütfen 6 haneli doğrulama kodunu girin.");
                return;
            }
            var self = this;
            this.loading = true;
            this.pinError = false;
            this.verifyEmailValidationPin(this.pin)
                .then(function () {
                    return self.refreshAuthUser(true);
                })
                .then(function () {
                    self.stopCountdown();
                    self.$message.success("E-posta adresiniz doğrulandı.");
                    self.handleClose();
                })
                .catch(function () {
                    self.pinError = true;
                    self.$message.error("Doğrulama kodu geçersiz veya süresi dolmuş.");
                })
                .finally(function () {
                    self.loading = false;
                });
        },
    },
};
</script>

<style lang="scss">
.email-validation-modal {
    .ant-modal-body {
        padding: 0;
    }
}

.email-validation {
    padding: 34px 36px 28px;

    &__icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 52px;
        height: 52px;
        margin-bottom: 18px;
        border-radius: 16px;
        background: #eefdfb;
        color: #1bc5bd;

        .material-icons {
            font-size: 28px;
        }
    }

    h2 {
        margin-bottom: 10px;
        color: #102a43;
        font-size: 24px;
        font-weight: 700;
    }

    &__lead {
        margin-bottom: 18px;
        color: #52606d;
        font-size: 15px;
        line-height: 1.6;
    }

    &__notice {
        padding: 16px 18px;
        margin-bottom: 20px;
        border: 1px solid #e6f4ff;
        border-radius: 14px;
        background: #f6fbff;
        color: #334e68;
        line-height: 1.6;

        p {
            margin-bottom: 10px;

            &:last-child {
                margin-bottom: 0;
            }
        }
    }

    &__step {
        margin-bottom: 24px;

        label {
            display: block;
            margin-bottom: 8px;
            color: #334e68;
            font-weight: 600;
        }

        &--pin {
            label {
                margin-bottom: 4px;
            }
        }
    }

    &__pin-card {
        padding: 18px;
        border: 1px solid #dbeafe;
        border-radius: 18px;
        background: linear-gradient(180deg, #f8fbff 0%, #ffffff 100%);
        box-shadow: 0 14px 34px rgba(16, 42, 67, 0.08);
    }

    &__pin-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 16px;
        margin-bottom: 18px;
    }

    &__sent-to {
        display: flex;
        align-items: center;
        gap: 6px;
        margin: 6px 0 0;
        color: #627d98;
        font-size: 14px;
        line-height: 1.5;

        .material-icons {
            font-size: 18px;
            color: #1bc5bd;
        }

        strong {
            color: #243b53;
            font-weight: 600;
        }
    }

    &__pin-group {
        display: flex;
        justify-content: center;
        gap: 12px;
        margin-bottom: 18px;
    }

    &__pin-box {
        width: 58px;
        height: 64px;
        padding: 0;
        border: 1px solid #cbd5e1;
        border-radius: 16px;
        background: #fff;
        color: #102a43;
        font-size: 28px;
        font-weight: 700;
        line-height: 62px;
        text-align: center;
        outline: none;
        transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, transform 0.2s ease;
        caret-color: #1890ff;

        &:focus {
            border-color: #1890ff;
            box-shadow: 0 10px 24px rgba(24, 144, 255, 0.18), 0 0 0 4px rgba(24, 144, 255, 0.12);
            transform: translateY(-1px);
        }

        &--filled {
            border-color: #40a9ff;
            background: linear-gradient(180deg, #f0f9ff 0%, #ffffff 100%);
            color: #096dd9;
        }

        &--error {
            border-color: #ff4d4f;
            background: #fff1f0;
            animation: pin-shake 0.4s ease;
        }

        &:disabled {
            background: #f7fafc;
            color: #9fb3c8;
            cursor: not-allowed;
        }
    }

    &__timer {
        display: flex;
        align-items: center;
        flex-shrink: 0;
        gap: 8px;
        min-width: 126px;
        padding: 9px 12px;
        border: 1px solid #bae7ff;
        border-radius: 14px;
        background: #eef8ff;
        color: #486581;
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);

        .material-icons {
            font-size: 20px;
            color: #1890ff;
        }

        small {
            display: block;
            margin-bottom: 1px;
            color: #627d98;
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 0.03em;
            text-transform: uppercase;
        }

        &--expired {
            border-color: #ffd591;
            background: #fff7e6;
            color: #ad6800;

            .material-icons {
                color: #fa8c16;
            }

            .email-validation__timer-value {
                color: #ad6800;
            }
        }
    }

    &__timer-value {
        min-width: 44px;
        color: #1890ff;
        font-family: "SF Mono", "Menlo", "Monaco", "Consolas", monospace;
        font-size: 18px;
        font-weight: 700;
        letter-spacing: 0.04em;
    }

    &__timer-progress {
        height: 7px;
        overflow: hidden;
        border-radius: 999px;
        background: #e6edf5;

        span {
            display: block;
            height: 100%;
            border-radius: inherit;
            background: linear-gradient(90deg, #1bc5bd 0%, #1890ff 100%);
            transition: width 0.3s ease;
        }
    }

    &__pin-helper {
        margin: 10px 0 0;
        color: #627d98;
        font-size: 13px;
        line-height: 1.5;
        text-align: center;
    }

    &__resend {
        display: block;
        width: 100%;
        margin-top: 12px;
        padding: 0;
        border: none;
        background: none;
        color: #1890ff;
        font-size: 14px;
        font-weight: 600;
        text-align: center;
        cursor: pointer;
        transition: color 0.2s ease;

        &:hover:not(:disabled) {
            color: #096dd9;
            text-decoration: underline;
        }

        &:disabled {
            color: #9fb3c8;
            cursor: not-allowed;
        }
    }

    &__actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        padding-top: 18px;
        border-top: 1px solid #edf2f7;
    }
}

@media (max-width: 576px) {
    .email-validation {
        &__pin-header {
            flex-direction: column;
        }

        &__timer {
            width: 100%;
        }

        &__pin-group {
            gap: 8px;
        }

        &__pin-box {
            width: 42px;
            height: 52px;
            border-radius: 12px;
            font-size: 22px;
            line-height: 50px;
        }
    }
}

@keyframes pin-shake {
    0%,
    100% {
        transform: translateX(0);
    }
    25% {
        transform: translateX(-4px);
    }
    75% {
        transform: translateX(4px);
    }
}
</style>
